// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { Space } from 'antd';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Timeline/CustomEventDrawer/extractData';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomEventType } from 'thehive-sdk';
import { TimelineEventList } from '../eventList';
import { ICONS } from '@Constants/iconsApp';
import useValidateForm from '@Hooks/useValidateForm';

interface CustomEventDrawerProps {
    data: CustomEventType;
    eventList: TimelineEventList;
    triggerElem?: ReactElement;
    defaultVisible?: boolean;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    hasPermission?: boolean;
}

export const CustomEventDrawer = ({
    data,
    eventList,
    triggerElem,
    defaultVisible = false,
    visible,
    onVisibleChange,
    hasPermission = false,
}: CustomEventDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const { _id } = data;
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    useEffect(() => {
        form.resetFields();
    }, [data, form]);

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.DateIcon />
                    {i('timeline.drawer.title-preview')}
                </Space>
            }
            triggerElem={triggerElem}
            className="drawer-form"
            width="50vw"
            visible={visible}
            disableClose={fieldsTouched}
            afterVisibleChange={(visibility) => {
                form.resetFields();
                onVisibleChange?.(visibility);
            }}
            defaultVisible={defaultVisible}
        >
            {(toggleDrawer) => (
                <THForm<CustomEventType>
                    name="custom-event-drawer-form"
                    form={form}
                    editable
                    initialValues={data}
                    //eslint-disable-next-line
                    onFinish={({ _createdAt, _updatedAt, ...data }) => {
                        eventList?.onEdit(data, _id);
                        toggleDrawer();
                    }}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                >
                    <THForm.Layout
                        footer={
                            fieldsTouched && (
                                <FormSubmitButton
                                    onCancel={() => {
                                        form.resetFields();
                                        toggleDrawer();
                                        setFieldsTouched(false);
                                    }}
                                    disabled={!isValid || !fieldsTouched}
                                    footerText={i('general.confirm')}
                                    formName="custom-event-drawer-form"
                                />
                            )
                        }
                    >
                        <Details
                            data={data}
                            extract={extractData({
                                local: i,
                                hasPermission,
                            })}
                        />
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
